import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "picker" }
const _hoisted_2 = { class: "row" }
const _hoisted_3 = {
  key: 0,
  class: "spacer source"
}
const _hoisted_4 = ["value", "options", "loading"]
const _hoisted_5 = {
  key: 0,
  class: "error-message"
}
const _hoisted_6 = {
  key: 1,
  class: "error-message"
}
const _hoisted_7 = {
  key: 1,
  class: "spacer"
}
const _hoisted_8 = ["value", "label"]
const _hoisted_9 = {
  key: 0,
  class: "error-message"
}
const _hoisted_10 = {
  key: 2,
  class: "spacer"
}
const _hoisted_11 = {
  key: 3,
  class: "spacer"
}
const _hoisted_12 = ["value", "label", "options", "loading"]
const _hoisted_13 = {
  key: 0,
  class: "error-message"
}
const _hoisted_14 = {
  key: 4,
  class: "spacer"
}
const _hoisted_15 = ["value", "label", "options", "loading"]
const _hoisted_16 = {
  key: 0,
  class: "error-message"
}
const _hoisted_17 = { class: "spacer" }
const _hoisted_18 = { key: 0 }
const _hoisted_19 = {
  key: 1,
  class: "error-message"
}
const _hoisted_20 = {
  key: 5,
  class: "commits-table mt-20"
}
const _hoisted_21 = ["rows", "columns"]

import { ref, computed, watch, Ref } from 'vue';
import { useStore } from 'vuex';
import { makeCommitShaCell, makeCommitAuthorCell } from '../../utils/table-formatters';
import debounce from 'lodash/debounce';
import { useGitBaseUrl, useGitProxyUserType, useGitProxyRepos, useGitProxyBranches, useGitProxyCommits } from '../../queries/useGitProxyQueries';
import { GitProxyGitRepo, GitProxyGitBranch, GitProxyGitCommit } from '../../models/gitproxy/ui-types';
import { ResourceQueryOptions } from '../../models/resource/ui-types';
import { GitConfig } from '../../models/gitconfig/ui-types';
import { AppFormSource } from 'models/application/ui-types';
import { useGitConfig } from '../../queries/useGitConfigQueries';
import { APPLICATION_SOURCE_TYPE } from '../../types';


export default /*@__PURE__*/_defineComponent({
  __name: 'GitPicker',
  props: {
    gitSource: {},
    type: {},
    updateSource: { type: Function },
    gitConfigs: {},
    gitConfigsForbidden: { type: Boolean },
    onGitConfigFilter: { type: Function },
    isLoadingGitConfigs: { type: Boolean },
    isErrorGitConfigs: { type: Boolean }
  },
  setup(__props: any) {

const GIT_BASE_URL = {
  [APPLICATION_SOURCE_TYPE.GIT_HUB]: 'https://github.com',
  [APPLICATION_SOURCE_TYPE.GIT_LAB]: 'https://gitlab.com',
};

const props = __props;

const store = useStore();
const t = store.getters['i18n/t'];

const gitType = computed(() => props.type as 'github' | 'gitlab');
const gitConfig = computed(() => props.gitSource?.gitConfig || null);

// Obtain full details of the selected repository, branch, and commit for use in subsequent operations
const selectedRepo = computed(() =>
  gitRepos.value?.find(
    repo => repo.name === props.gitSource?.repository
  ) ?? null
);
const selectedBranch = computed(() =>
  gitBranches.value?.find(
    branch => branch.name === props.gitSource?.branch
  ) ?? null
);
const selectedCommit = computed(() =>
  gitCommits.value?.find(
    commit => commit.commitId === props.gitSource?.commit
  ) ?? null
);

const gitConfigRequestOptions = ref<ResourceQueryOptions>({
  enabled: !!props.gitSource?.gitConfig,
  polling: false,
});
const {data: selectedGitConfig, isError: isErrorGitConfig} = useGitConfig(store, props.gitSource?.gitConfig || '', gitConfigRequestOptions);

const preparedRepos = computed(() =>
  (gitRepos.value || []).map((item) => ({ value: item.name, label: item.name }))
);

const preparedBranches = computed(() =>
  (gitBranches.value || []).map((item) => ({ value: item.name, label: item.name }))
);

const selectedCommitId = computed(() => selectedCommit.value?.commitId);

const gitConfigs = computed(() => (props.gitConfigs || []).filter((c: any) => c.provider.includes(props.type)));

const gitBaseUrl = useGitBaseUrl(gitType, selectedGitConfig); 

// Git User
const debouncedGitUserSearch = ref<string>(props.gitSource?.userOrOrg || '');
watch(
  () => props.gitSource?.userOrOrg,
  (newVal) => {
    onSearch(newVal || '');
  }
);
const onSearch = debounce(async (query: string) => {
  debouncedGitUserSearch.value = query;
}, 500);
const gitUserRequestOptions = computed<ResourceQueryOptions>(() => ({
  enabled: gitBaseUrl.value !== null && debouncedGitUserSearch.value !== '',
  polling: false,
}));
const { data: gitUser, isLoading: isGitUserLoading, isError: isGitUserError } = useGitProxyUserType(
  store,
  gitType,
  debouncedGitUserSearch,
  gitConfig,
  gitBaseUrl as Ref<string>,
  gitUserRequestOptions,
);

// Git Repository
const repoQuery = ref<string>('');
const debouncedGitRepoSearch = ref<string>('');
watch(repoQuery, (newQuery) => {
    onRepoSearch(newQuery || '');
});
const onRepoSearch = debounce(async (query: string) => {
  debouncedGitRepoSearch.value = query;
}, 500);
const gitRepoRequestOptions = computed<ResourceQueryOptions>(() => ({
  enabled: gitBaseUrl.value !== null && !!gitUser.value?.username,
  polling: false,
}));
const { data: gitRepos, isLoading: isGitReposLoading, isError: isGitReposError } = useGitProxyRepos(
  store,
  gitType,
  gitUser as Ref<{ username: string, userType: string | null }>,
  gitConfig,
  gitBaseUrl as Ref<string>,
  debouncedGitRepoSearch,
  gitRepoRequestOptions,
);

// Git Branch
const branchQuery = ref<string>('');
const debouncedGitBranchSearch = ref<string>('');
watch(branchQuery, (newQuery) => {
    onBranchSearch(newQuery || '');
});
const onBranchSearch = debounce(async (query: string) => {
  debouncedGitBranchSearch.value = query;
}, 500);
const gitBranchRequestOptions = computed<ResourceQueryOptions>(() => ({
  enabled: gitBaseUrl.value !== null && !!gitUser.value && !!selectedRepo.value,
  polling: false,
}));
const { data: gitBranches, isLoading: isGitBranchesLoading, isError: isGitBranchesError } = useGitProxyBranches(
  store,
  gitType,
  gitUser as Ref<{ username: string, userType: string | null }>,
  gitConfig,
  gitBaseUrl as Ref<string>,
  selectedRepo as Ref<GitProxyGitRepo>,
  debouncedGitBranchSearch,
  gitBranchRequestOptions,
);

// Git Commit
const gitCommitRequestOptions = computed<ResourceQueryOptions>(() => ({
  enabled: gitBaseUrl.value !== null && !!gitUser.value && !!selectedRepo.value && !!selectedBranch.value,
  polling: false,
}));
const { data: gitCommits, isLoading: isGitCommitsLoading, isError: isGitCommitsError } = useGitProxyCommits(
  store,
  gitType,
  gitUser as Ref<{ username: string, userType: string | null }>,
  gitConfig,
  gitBaseUrl as Ref<string>,
  selectedRepo as Ref<GitProxyGitRepo>,
  selectedBranch as Ref<GitProxyGitBranch>,
  gitCommitRequestOptions,
);

// Columns for trailhand-table
const columns = computed(() => [
  {
    field: 'commitId',
    label: t(`epinio.applications.gitSource.${ props.type }.tableHeaders.choose.label`),
    width: '60px',
    sortable: false,
    formatter: (_v: any, row: GitProxyGitCommit) => {
      const input = document.createElement('input') as HTMLInputElement;

      input.type = 'radio';
      input.name = 'commit-picker';
      input.value = row.commitId || '';
      input.checked = row.commitId === selectedCommitId.value;
      input.style.cursor = 'pointer';
      input.addEventListener('change', () => {props.updateSource(gitType.value, { commit: row.commitId })});

      return input;
    }
  },
  {
    field: 'sha',
    label: t(`epinio.applications.gitSource.${ props.type }.tableHeaders.sha.label`),
    width: '90px',
    sortable: false,
    formatter: (_v: any, row: GitProxyGitCommit) => makeCommitShaCell(row)
  },
  {
    field: 'author',
    label: t(`epinio.applications.gitSource.${ props.type }.tableHeaders.author.label`),
    width: '190px',
    sortable: false,
    formatter: (_v: any, row: GitProxyGitCommit) => makeCommitAuthorCell(
      row,
      t(`epinio.applications.gitSource.${ props.type }.tableHeaders.author.unknown`)
    )
  },
  {
    field: 'message',
    label: t(`epinio.applications.gitSource.${ props.type }.tableHeaders.message.label`),
    sortable: false,
  },
  {
    field: 'date',
    label: t(`epinio.applications.gitSource.${ props.type }.tableHeaders.date.label`),
    width: '220px',
    sortable: false,
    formatter: (_v: any, row: GitProxyGitCommit) => {
      const span = document.createElement('span');

      if (row.date) {
        span.textContent = new Date(row.date).toLocaleString();
      }

      return span;
    }
  },
]);

// Spread to create a new array reference when selectedCommitId changes,
// so Lit's hasChanged detects the update and re-renders the radio buttons.
const tableRows = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _sid = selectedCommitId.value;

  if (!gitCommits.value) return [];
  return [...gitCommits.value];
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (!_ctx.gitConfigsForbidden)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("trailhand-dropdown", {
              style: {"width":"100%"},
              value: gitConfig.value,
              options: (gitConfigs.value || []).map((c) => ({ value: c.meta.name, label: c.meta.name })),
              label: "Git Config",
              filterable: "",
              loading: _ctx.isLoadingGitConfigs,
              onDropdownChange: _cache[0] || (_cache[0] = (e) => { _ctx.updateSource(gitType.value, { gitConfig: e.detail.value }); }),
              onDropdownFilter: _cache[1] || (_cache[1] = (e) => { _ctx.onGitConfigFilter(e.detail.filter); })
            }, null, 40, _hoisted_4),
            _cache[7] || (_cache[7] = _createTextVNode()),
            (_ctx.isErrorGitConfigs)
              ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_unref(t)(`epinio.gitConfigs.errors.fetchAll`)), 1))
              : _createCommentVNode("", true),
            _cache[8] || (_cache[8] = _createTextVNode()),
            (_unref(isErrorGitConfig))
              ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(_unref(t)(`epinio.gitConfigs.errors.fetchOne`)), 1))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _cache[14] || (_cache[14] = _createTextVNode()),
      (_ctx.type === 'github' || (_ctx.type === 'gitlab' && !gitConfig.value))
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("trailhand-text-input", {
              style: {"width":"100%"},
              value: _ctx.gitSource?.userOrOrg || '',
              "data-testid": "git_picker-username-or-org",
              label: _unref(t)(`epinio.applications.gitSource.${ _ctx.type }.inputs.username.label`),
              required: true,
              onTextInputChange: _cache[2] || (_cache[2] = (e) => {
            _ctx.updateSource(gitType.value, { userOrOrg: e.detail.value, repository: '', branch: '', commit: '' }); 
          })
            }, null, 40, _hoisted_8),
            _cache[9] || (_cache[9] = _createTextVNode()),
            (_unref(isGitUserError))
              ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_unref(t)(`epinio.applications.gitSource.${ _ctx.type }.errors.noAccount`)), 1))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _cache[15] || (_cache[15] = _createTextVNode()),
      (_unref(isGitUserLoading))
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, _cache[10] || (_cache[10] = [
            _createElementVNode("trailhand-loading-spinner", null, null, -1)
          ])))
        : _createCommentVNode("", true),
      _cache[16] || (_cache[16] = _createTextVNode()),
      (_unref(gitUser) || (_ctx.type === 'gitlab' && gitConfig.value))
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createElementVNode("trailhand-dropdown", {
              style: {"width":"100%"},
              value: _ctx.gitSource?.repository || '',
              "data-testid": "git_picker-repo",
              label: _unref(t)(`epinio.applications.gitSource.${ _ctx.type }.inputs.repo.label`),
              required: true,
              options: preparedRepos.value,
              filterable: "",
              loading: _unref(isGitReposLoading),
              onDropdownChange: _cache[3] || (_cache[3] = (e) => {
            const repo = _unref(gitRepos)?.find(r => r.name === e.detail.value);
            if (!repo) return;

            const url = gitType.value === 'gitlab'
              ? (repo.webUrl || `${GIT_BASE_URL[gitType.value]}/${repo.pathWithNamespace}`)
              : (repo.htmlUrl || `${GIT_BASE_URL[gitType.value]}/${_ctx.gitSource?.userOrOrg}/${repo.name}`)
            _ctx.updateSource(gitType.value, { repository: repo.name, url, branch: '', commit: '' });
          }),
              onDropdownFilter: _cache[4] || (_cache[4] = (e) => { repoQuery.value = e.detail.filter; })
            }, null, 40, _hoisted_12),
            _cache[11] || (_cache[11] = _createTextVNode()),
            (_unref(isGitReposError))
              ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(_unref(t)(`epinio.applications.gitSource.${ _ctx.type }.errors.noRepo`)), 1))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _cache[17] || (_cache[17] = _createTextVNode()),
      (selectedRepo.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
            _createElementVNode("trailhand-dropdown", {
              style: {"width":"100%"},
              value: _ctx.gitSource?.branch || '',
              "data-testid": "git_picker-branch",
              label: _unref(t)(`epinio.applications.gitSource.${ _ctx.type }.inputs.branch.label`),
              required: true,
              options: preparedBranches.value,
              filterable: "",
              loading: _unref(isGitBranchesLoading),
              onDropdownChange: _cache[5] || (_cache[5] = (e) => {
            _ctx.updateSource(gitType.value, { branch: e.detail.value, commit: '' });
          }),
              onDropdownFilter: _cache[6] || (_cache[6] = (e) => { branchQuery.value = e.detail.filter; })
            }, null, 40, _hoisted_15),
            _cache[12] || (_cache[12] = _createTextVNode()),
            (_unref(isGitBranchesError))
              ? (_openBlock(), _createElementBlock("p", _hoisted_16, _toDisplayString(_unref(t)(`epinio.applications.gitSource.${ _ctx.type }.errors.noBranch`)), 1))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      _cache[18] || (_cache[18] = _createTextVNode()),
      _createElementVNode("div", _hoisted_17, [
        (_unref(isGitCommitsLoading))
          ? (_openBlock(), _createElementBlock("trailhand-loading-spinner", _hoisted_18))
          : _createCommentVNode("", true),
        _cache[13] || (_cache[13] = _createTextVNode()),
        (_unref(isGitCommitsError))
          ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(_unref(t)(`epinio.applications.gitSource.${ _ctx.type }.errors.noCommits`)), 1))
          : _createCommentVNode("", true)
      ]),
      _cache[19] || (_cache[19] = _createTextVNode()),
      (selectedBranch.value && _unref(gitCommits)?.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
            _createElementVNode("trailhand-table", {
              rows: tableRows.value,
              columns: columns.value,
              "key-field": "sha",
              searchable: true,
              paginated: true,
              "rows-per-page": 10
            }, null, 8, _hoisted_21)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})